
import React, { useState, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface User {
  full_name: string;
  rsvp_status?: 'accepted' | 'declined' | 'invited' | 'maybe';
  isHost?: boolean;
  avatar_url?: string | null;
  guest_avatar_url?: string | null;
}

interface CustomAvatarGroupProps {
  users: User[];
  maxVisible?: number;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const sizeClasses = {
  sm: "h-8 w-8 -ml-2.5",
  md: "h-10 w-10 -ml-3.5",
  lg: "h-12 w-12 -ml-4.5",
};

export const CustomAvatarGroup = ({
  users,
  maxVisible = 4,
  size = "md",
  className,
}: CustomAvatarGroupProps) => {
  const visibleUsers = users.slice(0, maxVisible);
  const remainingCount = Math.max(0, users.length - maxVisible);
  
  const getInitials = (fullName: string) => {
    return fullName
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'G';
  };

  // Border style only - don't include background styles here
  const getAvatarBorderStyle = (user: User) => {
    if (user.isHost) {
      return "border-2 border-amber-400";
    } else if (user.rsvp_status === 'accepted') {
      return "border-2 border-white";
    } else if (user.rsvp_status === 'declined') {
      return "border-2 border-black";
    }
    return "border-2 border-black";
  };

  // Background styles only for the fallback
  const getFallbackStyles = (user: User) => {
    if (user.isHost) {
      return "bg-black text-white";
    } else if (user.rsvp_status === 'accepted') {
      return "bg-black text-white";
    } else if (user.rsvp_status === 'declined') {
      return "bg-gray-200 text-black";
    }
    return "bg-white text-black";
  };

  return (
    <div className={cn("flex items-center", className)}>
      <div className="flex">
        {visibleUsers.map((user, index) => (
          <AvatarWithFallback
            key={index}
            user={user}
            index={index}
            sizeClass={sizeClasses[size]}
            getAvatarBorderStyle={getAvatarBorderStyle}
            getFallbackStyles={getFallbackStyles}
            getInitials={getInitials}
          />
        ))}
        
        {remainingCount > 0 && (
          <div
            className={cn(
              sizeClasses[size],
              "rounded-full flex items-center justify-center -ml-3.5 z-0",
              users.length > 0 && users[0].rsvp_status === 'accepted'
                ? "bg-black text-white border-2 border-white"
                : users.length > 0 && users[0].isHost
                  ? "bg-black text-white border-2 border-amber-400"
                  : "bg-white text-black border-2 border-black"
            )}
          >
            <span className="text-sm font-medium">
              +{remainingCount}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper component to handle image loading states and fallbacks
interface AvatarWithFallbackProps {
  user: User;
  index: number;
  sizeClass: string;
  getAvatarBorderStyle: (user: User) => string;
  getFallbackStyles: (user: User) => string;
  getInitials: (fullName: string) => string;
}

const AvatarWithFallback = ({
  user,
  index,
  sizeClass,
  getAvatarBorderStyle,
  getFallbackStyles,
  getInitials
}: AvatarWithFallbackProps) => {
  const [guestImageError, setGuestImageError] = useState(false);
  const [userImageError, setUserImageError] = useState(false);
  
  // Reset errors if user/urls change
  useEffect(() => {
    setGuestImageError(false);
    setUserImageError(false);
  }, [user.guest_avatar_url, user.avatar_url]);
  
  const hasGuestAvatar = !!user.guest_avatar_url && !guestImageError;
  const hasUserAvatar = !!user.avatar_url && !userImageError;
  const showFallback = !hasGuestAvatar && !hasUserAvatar;
  
  return (
    <Avatar
      className={cn(
        sizeClass,
        getAvatarBorderStyle(user),
        "first:ml-0 rounded-full overflow-hidden relative",
        // We use higher z-indices for earlier avatars
        index === 0 ? "z-30" : 
        index === 1 ? "z-20" : 
        index === 2 ? "z-10" : "z-0"
      )}
    >
      {/* Try guest_avatar_url first */}
      {hasGuestAvatar && (
        <AvatarImage 
          src={user.guest_avatar_url || ""} 
          alt={user.full_name}
          onError={() => setGuestImageError(true)}
          className="object-cover h-full w-full"
        />
      )}
      
      {/* Try avatar_url if no guest avatar or it failed */}
      {!hasGuestAvatar && hasUserAvatar && (
        <AvatarImage 
          src={user.avatar_url || ""} 
          alt={user.full_name}
          onError={() => setUserImageError(true)}
          className="object-cover h-full w-full"
        />
      )}
      
      {/* Fallback to initials */}
      <AvatarFallback 
        className={cn(
          "font-medium rounded-full", 
          getFallbackStyles(user)
        )}
      >
        {getInitials(user.full_name)}
      </AvatarFallback>
    </Avatar>
  );
};
